/**
 * JLR (J4R / JLR export) mapping transforms for Prospection import.
 *
 * Owner blocks are detected by structure + ordinality, not absolute indexes.
 * Property address comes from "Adresse Immeuble"; the trailing Adresse/Ville
 * block is the repeated property address — never an owner.
 */

import { IGNORE_TARGET, IMPORT_TARGETS, mapRows, normalizeHeader } from "./prospectImportMapping.js";
import {
  extractJlrPropertyEnrichment,
  normalizeLotNumbers,
  parseBoundedBuildingLabel,
  parseCadastralDivision,
  parseConstructionYear,
  parseJlrActivityDate,
  parseJlrWholeDollarToCents,
  parseLandArea,
  parseMunicipalRollNumber,
  parseNumberOfFloors,
  normalizeRegistryActivityType,
} from "./prospectImportPropertyData.js";

export const JLR_DEFAULT_PROPERTY_PROVINCE = "QC";
export const MAX_IMPORT_OWNERS = 8;
export const PROPERTY_ADDRESS_MISMATCH = "property_address_mismatch";

const PROPERTY_ONLY_HEADERS = new Set([
  "adresse immeuble",
  "code postal immeuble",
  "ville immeuble",
  "evaluation",
  "contrepartie",
  "derniere transaction",
  "nom transaction",
  "categorie batiment",
  "type construction",
  "nombre logements",
  "nombre de logements",
  "nombre etages",
  "annee construction",
]);

const OWNER_SLOT_ORDER = Object.freeze([
  "lastName",
  "firstName",
  "civicNumber",
  "street",
  "borough",
  "postalCode",
  "address",
  "province",
  "city",
]);

/** Address slots that compete with a direct ownerMailingAddress column. */
export const OWNER_ADDRESS_SLOT_KEYS = Object.freeze([
  "address",
  "civicNumber",
  "street",
  "city",
  "province",
  "postalCode",
]);

export const OWNER_NAME_SLOT_KEYS = Object.freeze(["firstName", "lastName"]);

/**
 * @param {string} normalized
 * @returns {string | null}
 */
export function matchOwnerSlot(normalized) {
  const n = String(normalized || "");
  if (n === "nom") return "lastName";
  if (n === "prenom") return "firstName";
  if (n === "no civique" || n === "numero civique") return "civicNumber";
  if (n === "rue") return "street";
  if (n === "arrondissement") return "borough";
  if (n === "code postal") return "postalCode";
  if (n === "adresse") return "address";
  if (n === "province") return "province";
  if (n === "ville") return "city";
  return null;
}

function firstHeaderIndex(normalizedHeaders, values) {
  const wanted = Array.isArray(values) ? values : [values];
  for (let i = 0; i < normalizedHeaders.length; i += 1) {
    if (wanted.includes(normalizedHeaders[i])) return i;
  }
  return null;
}

function cell(row, index) {
  if (index == null || index < 0) return "";
  const raw = row?.[index];
  return raw == null ? "" : String(raw).trim();
}

/**
 * @param {string[]} headers
 * @returns {object[]}
 */
export function detectOwnerBlocks(headers) {
  const list = Array.isArray(headers) ? headers : [];
  const normalized = list.map((header) => normalizeHeader(header));
  const blocks = [];
  let i = 0;
  while (i < normalized.length) {
    if (normalized[i] !== "nom") {
      i += 1;
      continue;
    }
    const block = { lastName: i };
    const seen = new Set(["lastName"]);
    i += 1;
    while (i < normalized.length) {
      const n = normalized[i];
      if (n === "nom") break;
      if (PROPERTY_ONLY_HEADERS.has(n)) break;
      const slot = matchOwnerSlot(n);
      if (!slot) {
        i += 1;
        continue;
      }
      if (seen.has(slot)) break;
      block[slot] = i;
      seen.add(slot);
      i += 1;
      if (slot === "city" && (seen.has("address") || seen.has("postalCode"))) break;
    }
    if (block.firstName != null || block.address != null || block.city != null || block.postalCode != null) {
      blocks.push(block);
    }
  }
  return blocks;
}

/**
 * Trailing civic/street/address/city group without Nom/Prenom.
 * Never interpreted as an owner.
 *
 * @param {string[]} headers
 * @param {object[]} ownerBlocks
 */
export function detectRepeatedPropertyAddressBlock(headers, ownerBlocks) {
  const normalized = (Array.isArray(headers) ? headers : []).map((header) => normalizeHeader(header));
  const used = new Set();
  for (const block of ownerBlocks || []) {
    for (const value of Object.values(block)) {
      if (typeof value === "number") used.add(value);
    }
  }

  const runs = [];
  let start = null;
  for (let i = 0; i <= normalized.length; i += 1) {
    const inRun = i < normalized.length && !used.has(i);
    if (inRun && start == null) start = i;
    if (!inRun && start != null) {
      runs.push([start, i - 1]);
      start = null;
    }
  }

  let found = null;
  for (const [from, to] of runs) {
    const slots = {};
    const seen = new Set();
    let valid = true;
    for (let i = from; i <= to; i += 1) {
      const n = normalized[i];
      if (!n) continue;
      if (n === "nom" || n === "prenom") {
        valid = false;
        break;
      }
      const slot = matchOwnerSlot(n);
      if (!slot) continue;
      if (seen.has(slot)) {
        valid = false;
        break;
      }
      slots[slot] = i;
      seen.add(slot);
    }
    if (!valid) continue;
    if (slots.lastName != null || slots.firstName != null) continue;
    const hasAddress = slots.address != null;
    const hasCivicStreet = slots.civicNumber != null && slots.street != null;
    if (!hasAddress && !hasCivicStreet) continue;
    if (slots.address == null && slots.city == null && slots.postalCode == null) continue;
    found = slots;
  }
  return found;
}

export function composeOwnerName(firstName, lastName) {
  const first = firstName == null ? "" : String(firstName).trim();
  const last = lastName == null ? "" : String(lastName).trim();
  return [first, last].filter(Boolean).join(" ");
}

export function formatCanadianPostal(value) {
  const raw = String(value || "").trim().toUpperCase().replace(/\s+/g, "");
  if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(raw)) {
    return `${raw.slice(0, 3)} ${raw.slice(3)}`;
  }
  return String(value || "").trim();
}

export function composeOwnerMailingAddress({
  address,
  civicNumber,
  street,
  city,
  province,
  postalCode,
} = {}) {
  let line = address == null ? "" : String(address).trim();
  if (!line) {
    line = [civicNumber, street]
      .map((part) => (part == null ? "" : String(part).trim()))
      .filter(Boolean)
      .join(" ");
  }
  const cityT = city == null ? "" : String(city).trim();
  const provT = province == null ? "" : String(province).trim();
  const postalT = formatCanadianPostal(postalCode);
  const parts = [];
  if (line) parts.push(line);
  if (cityT) parts.push(cityT);
  if (provT) parts.push(provT);
  if (postalT) parts.push(postalT);
  return parts.join(", ");
}

function identityPart(value) {
  return normalizeHeader(value).replace(/\s+/g, " ");
}

export function ownerIdentityKey(owner) {
  const src = owner && typeof owner === "object" ? owner : {};
  return [
    identityPart(src.name),
    identityPart(src.mailingAddress),
    identityPart(src.postalCode).replace(/\s+/g, ""),
  ].join("|");
}

export function dedupeOwners(owners) {
  const list = Array.isArray(owners) ? owners : [];
  const seen = new Set();
  const out = [];
  for (const owner of list) {
    if (!owner || typeof owner !== "object") continue;
    const name = String(owner.name || "").trim();
    const mailingAddress = String(owner.mailingAddress || "").trim();
    if (!name && !mailingAddress) continue;
    const key = ownerIdentityKey(owner);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(owner);
    if (out.length >= MAX_IMPORT_OWNERS) break;
  }
  return out;
}

export function ownerGroupHasAddressSlots(group) {
  if (!group || typeof group !== "object") return false;
  return OWNER_ADDRESS_SLOT_KEYS.some((key) => Number.isInteger(group[key]));
}

export function ownerGroupHasNameSlots(group) {
  if (!group || typeof group !== "object") return false;
  return OWNER_NAME_SLOT_KEYS.some((key) => Number.isInteger(group[key]));
}

/**
 * JLR files with detected Prénom/Nom columns compose the primary owner name from
 * those slots — the single-column "Owner name" selector is redundant.
 * Generic files, and JLR without usable split-name slots, keep the selector.
 */
export function shouldHideTopLevelOwnerNameSelector(profile) {
  return profile?.sourceProfile === "jlr" && ownerGroupHasNameSlots(profile?.ownerGroups?.[0]);
}

export function ownerAddressModeFromProfile(profile) {
  return ownerGroupHasAddressSlots(profile?.ownerGroups?.[0]) ? "composite" : "single";
}

export function mappingWithoutTarget(mapping, target) {
  return (Array.isArray(mapping) ? mapping : []).map((item) => (item === target ? IGNORE_TARGET : item));
}

export function sanitizeOwnerGroupIndexes(group) {
  if (!group || typeof group !== "object" || Array.isArray(group)) return {};
  const next = {};
  for (const slot of OWNER_SLOT_ORDER) {
    if (Number.isInteger(group[slot])) next[slot] = group[slot];
  }
  return next;
}

export function clearPrimaryOwnerAddressSlots(profile) {
  const src = profile && typeof profile === "object" ? profile : {};
  const groups = Array.isArray(src.ownerGroups) ? src.ownerGroups.map((group) => sanitizeOwnerGroupIndexes(group)) : [];
  if (groups[0]) {
    for (const key of OWNER_ADDRESS_SLOT_KEYS) delete groups[0][key];
  }
  return {
    ...src,
    ownerGroups: groups.filter((group) => Object.keys(group).length > 0),
  };
}

export function setPrimaryOwnerGroupSlot(profile, slot, index) {
  if (!OWNER_SLOT_ORDER.includes(slot)) return profile;
  const src = profile && typeof profile === "object" ? profile : {};
  const groups = Array.isArray(src.ownerGroups)
    ? src.ownerGroups.map((group, groupIndex) => (groupIndex === 0 ? sanitizeOwnerGroupIndexes(group) : group))
    : [];
  if (!groups[0]) groups.unshift({});
  if (Number.isInteger(index) && index >= 0) groups[0][slot] = index;
  else delete groups[0][slot];
  return {
    ...src,
    ownerGroups: groups.filter((group, groupIndex) => groupIndex > 0 || Object.keys(group).length > 0),
  };
}

/**
 * Direct ↔ composite conflict: enabling composite clears ownerMailingAddress;
 * switching back to single removes primary address-slot config only.
 * Additional JLR owner groups are preserved. Name mapping stays independent.
 */
export function applyOwnerAddressMode(profile, mapping, mode) {
  if (mode === "composite") {
    return {
      profile: profile && typeof profile === "object" ? profile : {},
      mapping: mappingWithoutTarget(mapping, "ownerMailingAddress"),
    };
  }
  return {
    profile: clearPrimaryOwnerAddressSlots(profile),
    mapping: Array.isArray(mapping) ? mapping : [],
  };
}

export function previewOwnerMailingAddressFromRow(row, group) {
  if (!ownerGroupHasAddressSlots(group)) return "";
  return composeOwnerMailingAddress({
    address: cell(row, group.address),
    civicNumber: cell(row, group.civicNumber),
    street: cell(row, group.street),
    city: cell(row, group.city),
    province: cell(row, group.province),
    postalCode: formatCanadianPostal(cell(row, group.postalCode)),
  });
}

export function extractOwnersFromRow(row, ownerGroups, options = {}) {
  const groups = Array.isArray(ownerGroups) ? ownerGroups : [];
  const primaryName = options.primaryName == null ? "" : String(options.primaryName).trim();
  const primaryMailingAddress =
    options.primaryMailingAddress == null ? "" : String(options.primaryMailingAddress).trim();
  const owners = [];
  for (const [groupIndex, group] of groups.entries()) {
    const lastName = cell(row, group.lastName);
    const firstName = cell(row, group.firstName);
    let name = composeOwnerName(firstName, lastName);
    if (!name && groupIndex === 0 && primaryName) name = primaryName;
    const civicNumber = cell(row, group.civicNumber);
    const street = cell(row, group.street);
    const city = cell(row, group.city);
    const province = cell(row, group.province);
    const postalCode = formatCanadianPostal(cell(row, group.postalCode));
    let mailingAddress = composeOwnerMailingAddress({
      address: cell(row, group.address),
      civicNumber,
      street,
      city,
      province,
      postalCode,
    });
    if (!mailingAddress && groupIndex === 0 && primaryMailingAddress) mailingAddress = primaryMailingAddress;
    if (!name && !mailingAddress) continue;
    const owner = { name, mailingAddress };
    if (civicNumber) owner.civicNumber = civicNumber;
    if (street) owner.street = street;
    if (city) owner.city = city;
    if (province) owner.province = province;
    if (postalCode) owner.postalCode = postalCode;
    owners.push(owner);
  }
  return dedupeOwners(owners);
}

function fieldIndex(profile, key) {
  const field = profile?.fields?.[key];
  if (field?.kind === "column" && Number.isInteger(field.index)) return field.index;
  return null;
}

export function buildJlrMappingProfile(headers, detection) {
  const list = Array.isArray(headers) ? headers : [];
  const normalized = list.map((header) => normalizeHeader(header));
  const ownerGroups = detection?.ownerBlocks || detectOwnerBlocks(list);
  const propertyFallback =
    detection?.repeatedPropertyBlock || detectRepeatedPropertyAddressBlock(list, ownerGroups);

  const fields = {
    address: { kind: "column", index: firstHeaderIndex(normalized, "adresse immeuble") },
    postalCode: { kind: "column", index: firstHeaderIndex(normalized, "code postal immeuble") },
    city: { kind: "column", index: firstHeaderIndex(normalized, "ville immeuble") },
    numberOfUnits: {
      kind: "column",
      index: firstHeaderIndex(normalized, ["nombre logements", "nombre de logements"]),
    },
    province: { kind: "constant", value: JLR_DEFAULT_PROPERTY_PROVINCE },
    evaluation: { kind: "column", index: firstHeaderIndex(normalized, "evaluation") },
    contrepartie: { kind: "column", index: firstHeaderIndex(normalized, "contrepartie") },
    derniereTransaction: {
      kind: "column",
      index: firstHeaderIndex(normalized, "derniere transaction"),
    },
    nomTransaction: { kind: "column", index: firstHeaderIndex(normalized, "nom transaction") },
    categorieBatiment: { kind: "column", index: firstHeaderIndex(normalized, "categorie batiment") },
    typeConstruction: { kind: "column", index: firstHeaderIndex(normalized, "type construction") },
    nombreEtages: { kind: "column", index: firstHeaderIndex(normalized, "nombre etages") },
    anneeConstruction: { kind: "column", index: firstHeaderIndex(normalized, "annee construction") },
    lotNumbers: {
      kind: "column",
      index: firstHeaderIndex(normalized, ["numero lot", "numeros lot", "lot", "lots"]),
    },
    cadastralDivision: {
      kind: "column",
      index: firstHeaderIndex(normalized, [
        "circonscription",
        "circonscription fonciere",
        "cadastre",
      ]),
    },
    municipalRollNumber: {
      kind: "column",
      index: firstHeaderIndex(normalized, ["matricule", "numero matricule"]),
    },
    landAreaValue: {
      kind: "column",
      index: firstHeaderIndex(normalized, ["superficie", "superficie terrain", "land area"]),
    },
    landAreaUnit: {
      kind: "column",
      index: firstHeaderIndex(normalized, ["unite superficie", "land area unit"]),
    },
  };

  return {
    sourceProfile: "jlr",
    confidence: detection?.confidence ?? 0,
    reasons: Array.isArray(detection?.reasons) ? detection.reasons : [],
    fields,
    ownerGroups,
    propertyFallback,
  };
}

export function suggestJlrColumnMapping(headers, profile) {
  const list = Array.isArray(headers) ? headers : [];
  const mapping = list.map(() => IGNORE_TARGET);
  const assign = (index, target) => {
    if (!Number.isInteger(index) || index < 0 || index >= mapping.length) return;
    if (!IMPORT_TARGETS.includes(target)) return;
    const previous = mapping.indexOf(target);
    if (previous >= 0) mapping[previous] = IGNORE_TARGET;
    mapping[index] = target;
  };
  assign(fieldIndex(profile, "address"), "address");
  assign(fieldIndex(profile, "city"), "city");
  assign(fieldIndex(profile, "postalCode"), "postalCode");
  assign(fieldIndex(profile, "numberOfUnits"), "numberOfUnits");
  assign(fieldIndex(profile, "evaluation"), "evaluation");
  assign(fieldIndex(profile, "contrepartie"), "contrepartie");
  assign(fieldIndex(profile, "derniereTransaction"), "derniereTransaction");
  assign(fieldIndex(profile, "nomTransaction"), "nomTransaction");
  assign(fieldIndex(profile, "categorieBatiment"), "categorieBatiment");
  assign(fieldIndex(profile, "typeConstruction"), "typeConstruction");
  assign(fieldIndex(profile, "nombreEtages"), "nombreEtages");
  assign(fieldIndex(profile, "anneeConstruction"), "anneeConstruction");
  assign(fieldIndex(profile, "lotNumbers"), "lotNumbers");
  assign(fieldIndex(profile, "cadastralDivision"), "cadastralDivision");
  assign(fieldIndex(profile, "municipalRollNumber"), "municipalRollNumber");
  assign(fieldIndex(profile, "landAreaValue"), "landAreaValue");
  assign(fieldIndex(profile, "landAreaUnit"), "landAreaUnit");
  return mapping;
}

function normalizeAddressForCompare(value) {
  return normalizeHeader(value);
}

function fallbackValue(row, fallback, slot) {
  if (!fallback || !Number.isInteger(fallback[slot])) return "";
  const raw = cell(row, fallback[slot]);
  return slot === "postalCode" ? formatCanadianPostal(raw) : raw;
}

export function applyJlrRowTransform(baseRow, rawRow, profile) {
  const next = { ...baseRow };
  const fallback = profile?.propertyFallback || null;

  if (!next.province && profile?.fields?.province?.kind === "constant") {
    next.province = profile.fields.province.value || JLR_DEFAULT_PROPERTY_PROVINCE;
  }

  const canonicalAddress = next.address || "";
  const fallbackAddress = fallbackValue(rawRow, fallback, "address");
  if (!canonicalAddress && fallbackAddress) {
    next.address = fallbackAddress;
    if (!next.city) {
      const fallbackCity = fallbackValue(rawRow, fallback, "city");
      if (fallbackCity) next.city = fallbackCity;
    }
    if (!next.postalCode) {
      const fallbackPostal = fallbackValue(rawRow, fallback, "postalCode");
      if (fallbackPostal) next.postalCode = fallbackPostal;
    }
  } else if (
    canonicalAddress &&
    fallbackAddress &&
    normalizeAddressForCompare(canonicalAddress) !== normalizeAddressForCompare(fallbackAddress)
  ) {
    const warnings = Array.isArray(next.warnings) ? [...next.warnings] : [];
    if (!warnings.includes(PROPERTY_ADDRESS_MISMATCH)) warnings.push(PROPERTY_ADDRESS_MISMATCH);
    next.warnings = warnings;
  }

  const { enrichment, warnings: enrichWarnings } = extractJlrPropertyEnrichment(rawRow, profile);
  if (enrichWarnings.length) {
    const warnings = Array.isArray(next.warnings) ? [...next.warnings] : [];
    for (const code of enrichWarnings) {
      if (!warnings.includes(code)) warnings.push(code);
    }
    next.warnings = warnings;
  }
  next.enrichment = enrichment;

  return applyOwnerGroupsToMappedRow(next, rawRow, profile);
}

/**
 * Project ownerGroups onto a mapped row for any source profile.
 * Name: firstName+lastName when present, otherwise direct ownerName.
 * Mailing: composed group address when present, otherwise direct ownerMailingAddress.
 */
export function applyOwnerGroupsToMappedRow(baseRow, rawRow, profile) {
  const next = { ...baseRow };
  const owners = extractOwnersFromRow(rawRow, profile?.ownerGroups, {
    primaryName: next.ownerName,
    primaryMailingAddress: next.ownerMailingAddress,
  });
  if (!owners.length) return next;
  next.owners = owners;
  if (owners[0].name) next.ownerName = owners[0].name;
  if (owners[0].mailingAddress) next.ownerMailingAddress = owners[0].mailingAddress;
  return next;
}

function ensurePrimaryOwnerArray(row) {
  const src = row && typeof row === "object" ? row : {};
  if (Array.isArray(src.owners) && src.owners.length) return src;
  const name = String(src.ownerName || "").trim();
  const mailingAddress = String(src.ownerMailingAddress || "").trim();
  if (!name && !mailingAddress) return src;
  return { ...src, owners: [{ name, mailingAddress }] };
}

function enrichmentFromMappedRow(row) {
  const src = row && typeof row === "object" ? row : {};
  if (src.enrichment && typeof src.enrichment === "object") return src;

  const evaluation = parseJlrWholeDollarToCents(src.evaluation);
  const consideration = parseJlrWholeDollarToCents(src.contrepartie);
  const activityDate = parseJlrActivityDate(src.derniereTransaction);
  const activityType = normalizeRegistryActivityType(src.nomTransaction);
  const buildingCategory = parseBoundedBuildingLabel(src.categorieBatiment);
  const constructionType = parseBoundedBuildingLabel(src.typeConstruction);
  const floors = parseNumberOfFloors(src.nombreEtages);
  const year = parseConstructionYear(src.anneeConstruction);
  const lots = normalizeLotNumbers(src.lotNumbers);
  const division = parseCadastralDivision(src.cadastralDivision);
  const roll = parseMunicipalRollNumber(src.municipalRollNumber);
  const landArea = parseLandArea(src.landAreaValue, src.landAreaUnit || "m2");

  const warnings = Array.isArray(src.warnings) ? [...src.warnings] : [];
  const push = (ok, code) => {
    if (!ok && !warnings.includes(code)) warnings.push(code);
  };
  push(evaluation.ok, "evaluation_invalid");
  push(consideration.ok, "contrepartie_invalid");
  push(activityDate.ok, "transaction_date_invalid");
  push(activityType.ok, "transaction_type_invalid");
  push(buildingCategory.ok, "building_category_invalid");
  push(constructionType.ok, "construction_type_invalid");
  push(floors.ok, "floors_invalid");
  push(year.ok, "construction_year_invalid");
  push(lots.ok, "lot_numbers_invalid");
  push(division.ok, "cadastral_division_invalid");
  push(roll.ok, "municipal_roll_invalid");
  push(landArea.ok, "land_area_invalid");

  const enrichment = {
    buildingCategory: buildingCategory.ok ? buildingCategory.value : null,
    constructionType: constructionType.ok ? constructionType.value : null,
    numberOfFloors: floors.ok ? floors.floors : null,
    constructionYear: year.ok ? year.year : null,
    lotNumbers: lots.ok ? lots.lotNumbers : [],
    cadastralDivision: division.ok ? division.value : null,
    municipalRollNumber: roll.ok ? roll.value : null,
    landArea: landArea.ok ? landArea.landArea : null,
    municipalAssessmentTotalCents: evaluation.ok ? evaluation.cents : null,
    registryActivity: null,
  };

  if (
    activityDate.ok &&
    activityType.ok &&
    (activityType.activityType || activityDate.isoDate || (consideration.ok && consideration.cents != null))
  ) {
    if (activityDate.ok) {
      enrichment.registryActivity = {
        activityType: activityType.activityType,
        originalLabel: activityType.originalLabel,
        activityDate: activityDate.isoDate,
        considerationCents: consideration.ok ? consideration.cents : null,
        registrationNumber: null,
        transferredPercentage: null,
      };
    }
  }

  const hasAny =
    enrichment.buildingCategory ||
    enrichment.constructionType ||
    enrichment.numberOfFloors != null ||
    enrichment.constructionYear != null ||
    enrichment.lotNumbers.length ||
    enrichment.cadastralDivision ||
    enrichment.municipalRollNumber ||
    enrichment.landArea ||
    enrichment.municipalAssessmentTotalCents != null ||
    enrichment.registryActivity;

  if (!hasAny) return warnings.length ? { ...src, warnings } : src;
  return { ...src, enrichment, warnings: warnings.length ? warnings : src.warnings };
}

/**
 * Apply the reviewed mapping.
 * Owner groups are extracted for ANY source profile that has profile.ownerGroups
 * (JLR detection, generic composite, or Vertex). JLR still adds QC default and
 * property-address fallback. Direct ownerName / ownerMailingAddress stay the
 * primary projection and stay aligned with owners[0].
 *
 * @param {string[][]} rows
 * @param {string[]} mapping
 * @param {object | null} profile
 */
export function mapRowsWithProfile(rows, mapping, profile) {
  const mapped = mapRows(rows, mapping);
  const list = Array.isArray(rows) ? rows : [];
  const hasOwnerGroups = Array.isArray(profile?.ownerGroups) && profile.ownerGroups.length > 0;
  return mapped.map((row, index) => {
    const raw = list[index] || [];
    let next = row;
    if (profile?.sourceProfile === "jlr") {
      next = applyJlrRowTransform(next, raw, profile);
    } else if (hasOwnerGroups) {
      next = applyOwnerGroupsToMappedRow(next, raw, profile);
      next = enrichmentFromMappedRow(next);
    } else {
      next = enrichmentFromMappedRow(next);
    }
    return ensurePrimaryOwnerArray(next);
  });
}

export { OWNER_SLOT_ORDER };
